import React, { useCallback, useState } from "react";
import ReactDataGrid from "@inovua/reactdatagrid-community";
import PaginationToolbar from "@inovua/reactdatagrid-community/packages/PaginationToolbar";
import "@inovua/reactdatagrid-community/index.css";
import "../css/boxPayGrid.css";
import { AiOutlineArrowUp, AiOutlineArrowDown } from "react-icons/ai";
import { useEffect } from "react";
import { BiEdit } from "react-icons/bi";
import CustomizeModal from "./modals/customizeModal";
import { Skeleton } from "@mui/material";
import { colorBrandObject } from "../../constants/brandings";
import { readableColor } from "polished";
import LoadingLogoPage from "./loadingPage/loadingPage";
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import LastPageIcon from '@mui/icons-material/LastPage';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import { PAGE_LIST } from "./utils/utils";

const BoxPayGrid = (props) => {
  const showCustomizeButton = props?.showCustomizeButton !== false;
  const alwaysVisible = props?.schema?.metadata?.defaultVisible;
  const { pageNo, setPageNo, pageLimit, setPageLimit, totalDataCount, customPagination = false, emptyText, tablePagination } = props;

  const gridStyle = {
    minHeight: props.schema.metadata.minHeight,
    position: props.schema.metadata.position,
  };

  //Customize dropdown
  const [schemaData, setSchemaData] = useState(props.schema.fields);
  const [checkedAll, setCheckedAll] = useState(false);
  const [updateAvailable, setUpdateAvailable] = useState([]);
  const maxPage = Math.ceil(totalDataCount / pageLimit);

  useEffect(() => {
    setSchemaData(props.schema.fields);
    const stored = localStorage.getItem("CustomizedFields");
    if (stored) {
      setUpdateAvailable(JSON.parse(stored));
    }
  }, [props]);

  useEffect(() => {
    if (updateAvailable) {
      updateAvailable.forEach((elem) => {
        const matched = schemaData.find(
          (item) => item?.header === elem?.header
        );
        if (matched) {
          matched.visible = true;
        }
      });
      setSchemaData([...schemaData]);
    }
  }, [updateAvailable]);

  const handleCheckboxChange = (option) => {
    setSchemaData(
      schemaData.map((elem) => {
        if (elem.name == option.name) {
          elem.visible = option.visible === undefined ? true : !option.visible;
          if (!elem.visible) {
            //unchecked select all checkbox
            setCheckedAll(false);
          }
        }
        return elem;
      })
    );
  };

  const handleSelectedAll = () => {
    setCheckedAll(!checkedAll);
    setSchemaData(
      props.schema.fields.map((elem) => {
        elem.visible = !checkedAll;
        return elem;
      })
    );
  };

  const [isOpen, setIsOpen] = useState(false);

  const updateSchemaData = (updatedSchemaData) => {
    updatedSchemaData.forEach((elem) => {
      const matched = schemaData.find((item) => item?.header === elem?.header);
      if (matched) {
        matched.visible = true;
      }
    });
    setSchemaData([...schemaData]);
    localStorage.setItem("CustomizedFields", JSON.stringify(updatedSchemaData));
  };

  const handleCloseCustomize = () => {
    setIsOpen(false);
  };

  const toggleDropdown = () => {
    setIsOpen(true);
  };

  // Customize Sorting button
  const arrowUpActiveStyle = {
    marginBottom: 8,
    marginRight: -3,
    color: colorBrandObject?.primaryButtonColor,
    height: "8",
  };

  const arrowUpStyle = {
    marginBottom: 8,
    marginRight: -3,
    color: "#707070",
    height: "8",
  };

  const arrowDownActiveStyle = {
    marginTop: 2,
    marginLeft: -10,
    color: colorBrandObject?.primaryButtonColor,
    height: "8",
  };

  const arrowDownStyle = {
    marginTop: 2,
    marginLeft: -10,
    color: "#707070",
    height: "8",
  };
  const defaultStyle = {
    marginLeft: 2,
    width: 8,
  };

  const SortIndicator = ({ direction }) => {
    return (
      <div style={defaultStyle}>
        {direction === 1 ? (
          <AiOutlineArrowUp style={arrowUpActiveStyle} />
        ) : (
          <AiOutlineArrowUp style={arrowUpStyle} />
        )}
        {direction === -1 ? (
          <AiOutlineArrowDown style={arrowDownActiveStyle} />
        ) : (
          <AiOutlineArrowDown style={arrowDownStyle} />
        )}
      </div>
    );
  };

  const renderSortTool = (direction) => {
    return <SortIndicator direction={direction} />;
  };

  const renderPaginationToolbar = useCallback((paginationProps) => {
    if (props.pagination === false) {
      return null;
    }
    return (
      <PaginationToolbar
        {...paginationProps}
        constrainTo=".InovuaReactDataGrid__body"
        renderCurrentPageInput={({ currentPage: page }) => (
          <>
            <div>&nbsp;&nbsp;{page}&nbsp;&nbsp;</div>
          </>
        )}
        bordered={false}
      />
    );
  }, []);

  useEffect(() => {
    setSchemaData(props.schema.fields);
  }, [props]);

  const customize = "customize";

  const dateComparator = (date1, date2) => {
    const date1Number = monthToComparableNumber(date1);
    const date2Number = monthToComparableNumber(date2);
    if (date1Number === null && date2Number === null) {
      return 0;
    }
    if (date1Number === null) {
      return -1;
    }
    if (date2Number === null) {
      return 1;
    }
    return date1Number - date2Number;
  };

  // eg 29/08/2004 gets converted to 20040829
  const monthToComparableNumber = (date) => {
    if (date === undefined || date === null) {
      return null;
    }
    const yearNumber = Number.parseInt(date.substring(6, 10));
    const monthNumber = Number.parseInt(date.substring(3, 5));
    const dayNumber = Number.parseInt(date.substring(0, 2));
    return yearNumber * 10000 + monthNumber * 100 + dayNumber;
  };

  const [columnDefs, setColumnDefs] = useState([
    { name: "addedDTime", comparator: dateComparator, direction: "1" },
  ]);

  const [loading, setLoading] = useState(true);
  const [hasData, setHasData] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (props?.data && props?.data?.length > 0) {
        await new Promise((resolve) => setTimeout(resolve, 500));
        setLoading(false);
        setHasData(props?.data?.length > 0);
      }
    };

    fetchData();
  }, [props]);

  useEffect(() => {
    if (props?.data && props?.data?.length === 0) {
      setLoading(false);
      setHasData([]);
    }
  }, [props]);

  const defaultSortInfo = {
    name: "addedDTime",
    comparator: dateComparator,
    direction: "1",
  };

  const handlePaginationChange = useCallback(({ skip, limit, currentPage }) => {
    // Determine if "Next Page" button is clicked
    console.log("Next Page Clicked!");
    // if (currentPage > currentPage) {
    //   // Trigger your desired function here
    //   // yourFunction();
    // }
    // setCurrentPage(currentPage); // Update the page state
  }, []);

  const handlePrevPage = () => {
    setPageNo(prev => prev > 1 ? prev - 1 : prev)
  }
  const handleNextPage = () => {
    setPageNo(prev => maxPage > prev ? prev + 1 : prev)
  }

  return (
    <>
      <div
        className="displayBox"
        id="boxPayGrid"
        style={{ marginBottom: "0px" }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            color: "rgba(1, 1, 2, 0.6)",
            fontFamily: "Poppins",
            fontWeight: "600",
            fontSize: "14px",
          }}
        >
          <div className="d-flex justify-items-start">
            {props?.schema?.metadata?.showTitle == true && (
              <h5>{props?.schema?.metadata?.title}</h5>
            )}
          </div>

          <div
            className="d-flex justify-items-end"
            style={{ marginRight: "-16px" }}
          >
            <div className="dropdown mr-lg-2 mr-sm-2">
              {showCustomizeButton && (
                <button
                  style={{
                    color: "rgba(1, 1, 2, 0.6)",
                    fontFamily: "Poppins",
                    fontWeight: "600",
                    fontSize: "14px",
                  }}
                  type="button"
                  className="btn "
                  onClick={toggleDropdown}
                >
                  <BiEdit />
                  Customize Columns
                </button>
              )}
            </div>
          </div>
        </div>
        {loading && !props.hideSkeleton && (
          <Skeleton
            variant="rectangular"
            width="100%"
            height={500}
            style={{ marginBottom: 10 }}
          />
        )}
        {!loading && hasData && (
          <>
            <ReactDataGrid
              style={gridStyle}
              columns={schemaData}
              dataSource={props.data}
              // defaultFilterValue={filterValue}
              defaultLimit={20}
              // onPaginationChange={handlePaginationChange}
              pagination={!customPagination && tablePagination}
              renderPaginationToolbar={renderPaginationToolbar}
              showCellBorders={false}
              showColumnMenuTool={false}
              // sortable={false}
              onColumnVisibleChange={({ column, visible }) =>
                (column.visible = visible)
              }
              rowHeight={props.schema.metadata.rowHeight}
              renderSortTool={renderSortTool}
              columnDefs={columnDefs}
              defaultSortInfo={props.schema.metadata.defaultSortInfo}
              columnUserSelect={true}
              emptyText={emptyText || "No data to display"}
              loadingText={() => <LoadingLogoPage />}
              renderLoading={() => <LoadingLogoPage />}
              loading={props.loader} // Enables the loader
              headerProps={{
                style: {
                  backgroundColor: colorBrandObject?.tableHeader, // Change header background color to red
                  color: readableColor(colorBrandObject?.tableHeader), // Optionally change the text color to white
                },
              }}
            />
            {
              customPagination && pageNo && pageLimit && (
                <>
                  <div className="custom-pagination" >
                    <div className="page-box">
                      <FirstPageIcon onClick={() => setPageNo(1)}
                        sx={{ color: pageNo === 1 ? '#cecece' : null }}
                      />
                      <KeyboardArrowLeftIcon onClick={handlePrevPage}
                        sx={{ color: pageNo === 1 ? '#cecece' : null }}
                      />
                      <span>{`Page `} {` ${pageNo} `} {` of `} {` ${maxPage}`}</span>
                      <KeyboardArrowRightIcon onClick={handleNextPage}
                        sx={{ color: pageNo === maxPage ? '#cecece' : null }}
                      />
                      <LastPageIcon onClick={() => setPageNo(maxPage)}
                        sx={{ color: pageNo === maxPage ? '#cecece' : null }}
                      />
                    </div>
                    <div className="pipe"></div>
                    <div className='resultBox'>
                      <div>
                        Results per page:
                      </div>
                      <Select
                        defaultValue={pageLimit}
                        sx={{ maxWidth: '100px', color: 'rgb(111, 111, 111)', borderRadius: '1px', maxWidth: '80px' }}
                        onChange={(e) => setPageLimit(e.target.value)}
                      >
                        {PAGE_LIST.map(i => <MenuItem value={i}>{i}</MenuItem>)}
                      </Select>
                    </div>
                    <div className="pipe"></div>
                    <div><span>Showing {totalDataCount === 0 ? 0 : ((pageNo - 1) * pageLimit) + 1} - {(pageNo * pageLimit) > totalDataCount ? totalDataCount : (pageNo * pageLimit)} of {totalDataCount}</span></div>
                  </div>
                </>
              )
            }
          </>
        )}
      </div>
      <CustomizeModal
        openModal={isOpen}
        onClose={handleCloseCustomize}
        schemaData={schemaData}
        props={props}
        alwaysVisible={alwaysVisible}
        onApply={updateSchemaData}
      />
    </>
  );
};

export default BoxPayGrid;
